import { differenceBy, trim } from 'lodash';
import { Node as SlateNode, Text as SlateText } from 'slate';

import {
  comparePlaceholderAndInstruction,
  isGenericPromptInstructionType,
  PROMPT_INSTRUCTION_TYPE,
} from 'src/data/generation';
import {
  createBlockQuote,
  createLinkNode,
  createParagraph,
  createTextNode,
  createTextParagraph,
  htmlToSlate,
  serializeVariableNode,
  SLATE_TEXT_FORMATS,
  slateToHtml,
  slateToText,
  textToSlate,
} from 'src/lib/slate';

import { PROMPT_TAG_SEARCH } from '../const';

import { createInstructionNode } from './templates';

/**
 * Converts Slate value to "instructions data" object
 *
 * @param {import('slate').Descendant[]} slate - Slate editor value
 * @returns {InstructionsData} - Instructions data object
 */
export const slateToInstructionsData = (slate) => {
  const text = slateToText(slate);
  const html = slateToHtml(slate);
  const { placeholders, updatedValue } = extractBankPlaceholders(slate);
  const serialized = slateToText(updatedValue);

  return {
    html,
    serialized,
    placeholders,
    text,
  };
};

/**
 * Merges two instructions data objects
 *
 * @param {InstructionsData} [data1] - First instructions data object
 * @param {InstructionsData} [data2] - Second instructions data object
 * @returns {InstructionsData} - Merged instructions data object
 */
export const mergeInstructionsData = (data1, data2) => {
  const html1 = (trim(data1?.text) !== '' && data1?.html) || '';
  const html2 = (trim(data2?.text) !== '' && data2?.html) || '';

  // take HTML values from both data1 and data2 and concatenate them
  const resultHtml = `${html1}${html2}`;

  // create new "data" object from resulting HTML
  const slateValue = htmlToSlate(resultHtml);
  return slateToInstructionsData(slateValue);
};

/**
 * Merges a list of structure instructions into instructions data object
 *
 * @param {InstructionsData} data - First instructions data object
 * @param {PromptInstruction[]} instructions - Second instructions data object
 * @returns {InstructionsData} - Merged instructions data object
 */
export const mergeStructureInstructionsIntoData = (data, instructions) => {
  const dataHtml = (trim(data?.text) !== '' && data?.html) || '';
  const placeholdersList = Object.values(data?.placeholders || {});

  // proceed only with instructions that are not in "data.placeholders" already
  const preparedInstructions = differenceBy(instructions, placeholdersList, 'id');

  // convert prepared instructions to "instructions data" object
  const instructionsData = createInstructionsDataFromStructureInstructions(preparedInstructions);
  const instructionHtml = (trim(instructionsData?.text) !== '' && instructionsData?.html) || '';

  // take HTML values from both "data" and "instructionsData" and concatenate them, placing "instructions" first
  const resultHtml = `${instructionHtml}${dataHtml}`;

  // create new "data" object from resulting HTML
  const slateValue = htmlToSlate(resultHtml);
  return slateToInstructionsData(slateValue);
};

/**
 * Merges text into instructions data object
 *
 * @param {InstructionsData} data - First instructions data object
 * @param {InstructionsData} instructions - Second instructions data object
 * @returns {InstructionsData} - Merged instructions data object
 */
export const mergeTextIntoData = (data, instructions) => {
  const instructionsHtml = instructions.html.replace(/<p>\s*<\/p>/g, '');
  if (instructionsHtml === '') {
    return data;
  }

  // Rebuilt from the merged HTML rather than concatenated field by field: appending the two texts
  // directly runs the last line of one into the first of the other, and "serialized" is what becomes
  // the instruction the model is given.
  const dataHtml = (trim(data?.text) !== '' && data?.html) || '';
  const slateValue = htmlToSlate(`${dataHtml}${instructionsHtml}`);
  return slateToInstructionsData(slateValue);
};

/**
 * Removes unavailable instruction from structure instructions data object
 *
 * @param {InstructionsData} data - Structure instructions data object
 * @param {{type: PROMPT_INSTRUCTION_TYPE, id: string}[]} availableInstructions - Available instructions
 * @returns {InstructionsData} - Updated instructions data object
 */
export const removeUnavailableInstructionsFromData = (data, availableInstructions) => {
  // take "placeholders" from the current "structure" value
  const placeholdersValues = data?.placeholders ? Object.values(data.placeholders) : [];

  // filter out all still available "instruction" placeholders
  const unavailablePlaceholders = placeholdersValues.filter((placeholder) => {
    // take only "instruction" placeholders
    if (placeholder.type !== SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION) {
      return false;
    }

    // take only "placeholders" with instructions that are not available in the current session
    const isAvailable = availableInstructions.some((item) =>
      comparePlaceholderAndInstruction(placeholder, item)
    );
    return !isAvailable;
  });
  const instructionsToRemove = unavailablePlaceholders.map((placeholder) => ({
    type: placeholder.instructionType,
    id: placeholder.id,
  }));

  // remove all un-available instructions from the current value
  return removeInstructionsFromData(data, instructionsToRemove);
};

/**
 * Removes instruction from structure instructions data object
 *
 * @param {InstructionsData} data - Structure instructions data object
 * @param {{type: PROMPT_INSTRUCTION_TYPE, id: string}[]} instructions - Instructions to remove
 * @returns {InstructionsData} - Updated instructions data object
 */
export const removeInstructionsFromData = (data, instructions) => {
  const removeInstructionNode = (nodes) =>
    nodes
      .filter((node) => {
        return !(
          node.type === SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION &&
          instructions.some((instr) => node.data?.type === instr.type && node.data?.id === instr.id)
        );
      })
      .map((node) => {
        if (node.children) {
          return {
            ...node,
            children: removeInstructionNode(node.children),
          };
        }
        return node;
      });

  const html = (trim(data?.text) !== '' && data?.html) || '';
  const slateValue = htmlToSlate(html);

  const newValue = removeInstructionNode(slateValue);

  return slateToInstructionsData(newValue);
};

/**
 * Create vocabulary instruction
 * @param {Object} brandVoice
 * @returns {PromptInstruction}
 */
export const createVocabularyInstruction = (brandVoice) => ({
  id: brandVoice.id,
  type: PROMPT_INSTRUCTION_TYPE.vocabulary,
  text: `Brand vocabulary from: ${brandVoice.name}`,
});

/**
 * Create format instruction
 * @param {Object} format
 * @returns {PromptInstruction}
 */
export const createFormatInstruction = (format) => ({
  id: format.id,
  type: PROMPT_INSTRUCTION_TYPE.format,
  text: `Format: ${format.name}`,
});

/**
 * Extracts instructions from placeholders
 *
 * @param {Object} params
 * @param {Object[]} params.placeholders - Placeholders to extract instructions from
 * @param {Object[]} params.customFormats - Custom formats
 * @param {Object[]} params.brandVoicesWithTerms - Brand voices with terms
 * @returns {PromptInstruction[]} - List of instructions
 */
export const extractInstructionsFromPlaceholders = ({
  placeholders,
  customFormats,
  brandVoicesWithTerms,
}) => {
  if (!placeholders || placeholders.length === 0) {
    return [];
  }

  const preparedFormats = customFormats || [];

  return placeholders
    .map((placeholder) => {
      if (placeholder.type !== SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION) {
        return null;
      }

      if (placeholder.instructionType === PROMPT_INSTRUCTION_TYPE.format) {
        const format = preparedFormats.find((format) => format.id === placeholder.id);
        if (format) {
          return createFormatInstruction(format);
        }
      } else if (placeholder.instructionType === PROMPT_INSTRUCTION_TYPE.vocabulary) {
        const brandVoice = brandVoicesWithTerms?.find((voice) => voice.id === placeholder.id);
        if (brandVoice) {
          return createVocabularyInstruction(brandVoice);
        }
      }

      return null;
    })
    .filter(Boolean);
};

/**
 * Creates "instructions data" from structure instructions list
 * @param {PromptInstruction[]} instructions - List of structure instructions
 * @returns {InstructionsData} - Instructions data object
 */
export const createInstructionsDataFromStructureInstructions = (instructions) => {
  const slateValue = instructions.map((instr) => createParagraph([createInstructionNode(instr)]));

  return slateToInstructionsData(slateValue);
};

const createPlaceholderKey = (index) => `<!#PH${index}#!>`;

const NODE_TO_PLACEHOLDER_MAP = {
  [SLATE_TEXT_FORMATS.BANK_MESSAGE]: (node) => ({
    type: SLATE_TEXT_FORMATS.BANK_MESSAGE,
    id: node.bankMessageId,
    body: node.bankMessageBody,
  }),

  [SLATE_TEXT_FORMATS.PRODUCT_DESCRIPTION]: (node) => ({
    type: SLATE_TEXT_FORMATS.PRODUCT_DESCRIPTION,
    title: node.title,
    article: node.article,
  }),

  [SLATE_TEXT_FORMATS.PROMPT_IMAGE]: (node) => ({
    type: SLATE_TEXT_FORMATS.PROMPT_IMAGE,
    title: 'image description',
    article: node.children[0].text,
    id: node.children[0].id || node.id,
  }),

  [SLATE_TEXT_FORMATS.PROMPT_VARIABLE]: (node) => ({
    type: SLATE_TEXT_FORMATS.PROMPT_VARIABLE,
    title: node.title || node.children[0].title,
    info: node.info || node.children[0].info,
    infoTooltip: node.infoTooltip || node.children[0].infoTooltip,
  }),

  [SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION]: (node) => {
    const placeholder = {
      type: SLATE_TEXT_FORMATS.PROMPT_INSTRUCTION,
      instructionType: node.data.type,
    };
    if (isGenericPromptInstructionType(node.data.type)) {
      placeholder.instruction = node.children[0].text;
    } else {
      placeholder.id = node.data.id;
    }
    return placeholder;
  },

  [SLATE_TEXT_FORMATS.PROMPT_DOCUMENT]: (node) => ({
    type: SLATE_TEXT_FORMATS.PROMPT_DOCUMENT,
    title: 'document content',
    article: node.children[0].text,
    id: node.children[0].id || node.id,
  }),

  [SLATE_TEXT_FORMATS.RESOURCE_URL]: (node) => ({
    type: SLATE_TEXT_FORMATS.RESOURCE_URL,
    url: node.resourceUrl,
  }),

  [SLATE_TEXT_FORMATS.PROMPT_AUDIO]: (node) => ({
    type: SLATE_TEXT_FORMATS.PROMPT_AUDIO,
    title: 'audio description',
    article: node.children[0].text,
    id: node.children[0].id || node.id,
  }),

  [SLATE_TEXT_FORMATS.PROMPT_VIDEO]: (node) => ({
    type: SLATE_TEXT_FORMATS.PROMPT_VIDEO,
    title: 'video description',
    article: node.children[0].text,
    id: node.children[0].id || node.id,
  }),
};

export const extractBankPlaceholders = (slateValue) => {
  const placeholders = {};
  let placeholderCounter = 0;

  const updatedValue = slateValue.map((paragraph) => {
    const updatedChildren = paragraph.children.map((node) => {
      const mapper = NODE_TO_PLACEHOLDER_MAP[node.type];
      if (mapper) {
        const key = createPlaceholderKey(++placeholderCounter);
        placeholders[key] = mapper(node);
        return createTextNode(key);
      }

      return node;
    });

    return {
      ...paragraph,
      children: updatedChildren,
    };
  });

  return { updatedValue, placeholders };
};

export const extractBlogSectionPlaceholders = ({ slateValue, initialCounter }) => {
  const placeholders = {};
  let placeholderCounter = initialCounter || 0;

  const processBlocks = (blocks) =>
    blocks.map((node) => {
      switch (node.type) {
        case SLATE_TEXT_FORMATS.LINK: {
          if (node.children?.[0]?.text) {
            const key = createPlaceholderKey(++placeholderCounter);
            placeholders[key] = {
              type: SLATE_TEXT_FORMATS.LINK,
              url: node.url,
              text: node.children?.[0]?.text || '',
            };
            return createTextNode(key);
          }
          return createTextNode('');
        }

        case SLATE_TEXT_FORMATS.BLOCK_QUOTE: {
          const key = createPlaceholderKey(++placeholderCounter);
          placeholders[key] = {
            type: SLATE_TEXT_FORMATS.BLOCK_QUOTE,
            text: SlateNode.string(node),
          };
          return createTextParagraph(key);
        }

        default: {
          if (node.children?.length > 0) {
            return {
              ...node,
              children: processBlocks(node.children),
            };
          }
          return node;
        }
      }
    });

  const updatedValue = processBlocks(slateValue);
  return { updatedValue, placeholders, counter: placeholderCounter };
};

/**
 * Convert text along with passed placeholders to Slate fragment
 * @param {string} generatedText - Text with placeholders
 * @param {object} [placeholders] - Placeholders data
 * @returns {import('slate').Element[]} - Slate fragment
 */
export const convertTextWithPlaceholdersToSlateFragment = ({ generatedText, placeholders }) => {
  if (!generatedText) {
    return [];
  }

  // Convert text to Slate fragment
  const fragment = textToSlate(generatedText);

  // If we don't have placeholders, we can return the fragment as is
  if (!placeholders || Object.keys(placeholders).length === 0) {
    return fragment;
  }

  const withRestoredQuotes = restoreQuotes({ fragment, placeholders });
  return restoreLinks({ fragment: withRestoredQuotes, placeholders });
};

const TAG_PROMPT_START = '<span data-prompt-tag="true">';
const TAG_PROMPT_END = '</span>';
export const replacePromptTags = (htmlString) => {
  if (!htmlString) {
    return;
  }

  const tagStrings = htmlString.split(TAG_PROMPT_START).slice(1);
  const tagReplacements = [];
  tagStrings.forEach((tagString) => {
    const tagContent = tagString.split(TAG_PROMPT_END)[0];
    if (tagContent.toLowerCase().includes(PROMPT_TAG_SEARCH.variable)) {
      const newTag = serializeVariableNode(
        {
          title: tagContent,
          children: [{ text: tagContent }],
        },
        tagContent
      );
      tagReplacements.push({
        replace: TAG_PROMPT_START + tagContent + TAG_PROMPT_END,
        replaceWith: newTag,
      });
    }
  });

  let finalValue = htmlString;
  tagReplacements.forEach((item) => {
    finalValue = finalValue.replace(item.replace, item.replaceWith);
  });

  return finalValue;
};

const MAX_FILE_NAME_LENGTH = 15;
export const replaceFilePlaceholders = (text, fields, field) => {
  let serialized = text;
  Object.entries(fields[field].placeholders).forEach(([placeholder, value]) => {
    if (fields[field].placeholders[placeholder].type === SLATE_TEXT_FORMATS.PROMPT_DOCUMENT) {
      const fileText = value.article;
      if (fileText) {
        const text =
          fileText.length > MAX_FILE_NAME_LENGTH
            ? `${fileText.substring(0, MAX_FILE_NAME_LENGTH)}\u2026`
            : fileText;
        serialized = serialized.replace(placeholder, `'${text}'`);
      }
    } else if (fields[field].placeholders[placeholder].type === SLATE_TEXT_FORMATS.PROMPT_IMAGE) {
      const fileText = value.article;
      if (fileText) {
        const text =
          fileText.length > MAX_FILE_NAME_LENGTH
            ? `${fileText.substring(0, MAX_FILE_NAME_LENGTH)}\u2026`
            : fileText;
        serialized = serialized.replace(placeholder, `'${text}'`);
      }
    } else if (fields[field].placeholders[placeholder].type === SLATE_TEXT_FORMATS.BANK_MESSAGE) {
      serialized = serialized.replace(
        placeholder,
        `'${fields[field].placeholders[placeholder]?.body}'`
      );
    } else if (fields[field].placeholders[placeholder].type === SLATE_TEXT_FORMATS.RESOURCE_URL) {
      serialized = serialized.replace(
        placeholder,
        `'${fields[field].placeholders[placeholder]?.url}'`
      );
    }
  });
  return serialized;
};

const restoreQuotes = ({ fragment, placeholders }) => {
  return fragment.map((node) => {
    const nodeText = SlateNode.string(node);
    const firstBlockQuotePlaceholder = getFirstBlockQuotePlaceholder(nodeText, placeholders);
    if (firstBlockQuotePlaceholder) {
      const placeholderData = placeholders[firstBlockQuotePlaceholder];
      if (placeholderData) {
        return createBlockQuote({ text: placeholderData.text });
      }
    }

    return node;
  });
};

const restoreLinks = ({ fragment, placeholders }) => {
  const result = [];
  fragment.forEach((node) => {
    // Replace links in text nodes
    if (SlateText.isText(node)) {
      const parts = textWithLinkPlaceholdersToSlateFragment({ text: node.text, placeholders });
      result.push(...parts);
      return;
    }

    // Recursively process children of some node types
    if (
      [
        SLATE_TEXT_FORMATS.PARAGRAPH,
        SLATE_TEXT_FORMATS.BULLETED_LIST,
        SLATE_TEXT_FORMATS.NUMBERED_LIST,
        SLATE_TEXT_FORMATS.LIST_ITEM,
      ].includes(node.type)
    ) {
      result.push({
        ...node,
        children: restoreLinks({ fragment: node.children, placeholders }),
      });
      return;
    }

    // Just take other nodes unchanged
    result.push(node);
  });
  return result;
};

const getFirstBlockQuotePlaceholder = (text, placeholders) => {
  const regex = /<!#PH.*?!>/;
  const match = text.match(regex);
  const firstBlockQuotePlaceholder = match?.[0] || null;
  const placeholderData = placeholders[firstBlockQuotePlaceholder];
  if (placeholderData?.type === SLATE_TEXT_FORMATS.BLOCK_QUOTE) {
    return firstBlockQuotePlaceholder;
  }
};

const textWithLinkPlaceholdersToSlateFragment = ({ text, placeholders }) => {
  const result = [];

  const parts = splitTextByLinkPlaceholders(text);
  parts.forEach((part) => {
    const placeholderData = placeholders[part];
    if (placeholderData) {
      result.push(createLinkNode({ url: placeholderData.url, text: placeholderData.text }));
    } else if (part !== '') {
      result.push(createTextNode(part));
    }
  });

  return result;
};

const splitTextByLinkPlaceholders = (text) => {
  const regex = /(<!#PH.*?!>)/;
  return text.split(regex);
};
